// metaDescriptorElements.jsx
'use strict';
import React from 'react';

import { EMPTY_ARRAY } from '../resource/defaults.js';

/**
 * Render hoistable head meta descriptors (title / meta / link) as React elements.
 * This is the single descriptor→element mapping shared by both sides:
 * the client renders these elements in the tree and React 19 hoists them into
 * <head>; the server calls renderToStaticMarkup() on the same elements to emit
 * the SSR <head> HTML, so the two sides can never drift.
 * {'script:ld+json'} descriptors are skipped — see renderJsonLdElements.
 * @param {object} params
 * @param {Array<object>} params.metaTags - meta descriptors; each is one of:
 *   { title } | { name, content } | { property, content } |
 *   { tagName: 'link', ... } | { 'script:ld+json': object }.
 * @returns {Array<React.ReactElement>} Hoistable head elements.
 * @example
 * renderHeadMetaElements({
 *   metaTags: [{ title: 'SWAG' }, { name: 'robots', content: 'noindex, nofollow' }],
 * });
 * // => [<title>SWAG</title>, <meta name="robots" content="noindex, nofollow" />]
 */
const renderHeadMetaElements = ({ metaTags = EMPTY_ARRAY }) =>
  metaTags
    .map((metaProps, index) => {
      if (!metaProps || metaProps['script:ld+json']) {
        return null;
      }
      if (metaProps.title) {
        return <title key={`title-${index}`}>{metaProps.title}</title>;
      }
      if (metaProps.tagName) {
        const { tagName, ...rest } = metaProps;
        const Component = tagName;
        return <Component key={`${tagName}-${index}`} {...rest} />;
      }
      return <meta key={`meta-${index}`} {...metaProps} />;
    })
    .filter(Boolean);

/**
 * Render {'script:ld+json'} meta descriptors as inline JSON-LD script elements.
 * Inline scripts are NOT hoisted by React 19, so these stay where they are
 * rendered (inside the route tree / <body>) — which Google supports for
 * structured data. Kept separate from renderHeadMetaElements so the server
 * can emit head HTML without duplicating the JSON-LD the app tree renders.
 * @param {object} params
 * @param {Array<object>} params.metaTags - meta descriptors (same shape as renderHeadMetaElements).
 * @returns {Array<React.ReactElement>} JSON-LD script elements.
 * @example
 * renderJsonLdElements({ metaTags: [{ 'script:ld+json': { '@type': 'WebSite' } }] });
 * // => [<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: '{"@type":"WebSite"}' }} />]
 */
const renderJsonLdElements = ({ metaTags = EMPTY_ARRAY }) =>
  metaTags
    .map((metaProps, index) => {
      if (!metaProps?.['script:ld+json']) {
        return null;
      }
      try {
        // dangerouslySetInnerHTML is required: React escapes quotes in text
        // children, which would break the JSON. Escape `<` to prevent
        // breaking out of the </script> context.
        const json = JSON.stringify(metaProps['script:ld+json']).replace(
          /</g,
          '\\u003c'
        );
        return (
          <script
            key={`jsonld-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: json }}
          />
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to stringify JSON-LD:', {
          error,
          jsonData: metaProps['script:ld+json'],
        });
        return null;
      }
    })
    .filter(Boolean);

export { renderHeadMetaElements, renderJsonLdElements };
